import { useRef } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import { a, useSprings } from 'react-spring';
import { useGesture } from 'react-use-gesture';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import clamp from 'lodash/fp/clamp';
import map from 'lodash/fp/map';
import styled from '@emotion/styled';
import { makeStyles } from '@material-ui/styles';

import * as featureFlagSelectors from 'dux/featureFlags/selectors';

import {
  conditioner,
  scalp_mask,
  shampoo,
  styling_gel,
  supplement_core,
} from 'assets/content/products';
import { productsSlugs } from 'constants/products';
import { srcOrNextImageProp } from 'PropTypes/imageProps';
import useMedia from 'utils/useMedia';

import BlockLayout from 'Blocks/BlockLayout';
import ProductsItem from 'Blocks/ProductsItem';
import Button from 'Components/LegacyButton';
import LazyImg from 'Components/LazyImg';
import { breakpoints } from 'Components/ThemeProvider';
import Typography from 'Components/Typography';

const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative', // For the floating image

    opacity: 0,

    '& h2': {
      marginBottom: 38,
    },

    '& a': {
      textDecoration: 'none',
    },
  },
  visible: {
    opacity: 1,
    transition: 'opacity 300ms ease-in',
  },
  cardHome: {
    justifyContent: 'space-between',
    minWidth: '100%',
    maxWidth: 'inherit',
  },
  imageContainerHome: {
    width: '100%',
    height: 'auto',
    marginBottom: 0,

    '& img': {
      position: 'relative',
    },

    '& h3': {
      backgroundColor: 'red',
    },

    [theme.breakpoints.match('sm')]: {
      maxWidth: 'inherit',
      maxHeight: 'inherit',
      marginTop: 0,
    },
  },
  wrapper: {
    display: 'flex',

    [theme.breakpoints.match('sm')]: {
      position: 'absolute',
      willChange: 'transform',

      width: 300,
      margin: '0 10px',

      '& a': {
        position: 'relative',
      },

      '& img': {
        display: 'block',
      },

      '& span,p': {
        position: 'absolute',
        bottom: 10,
        left: '50%',
        transform: 'translateX(-50%)',

        width: 'fit-content',

        ...theme.typography.p3,
        color: '#283333',
        fontStyle: 'italic',
      },

      '& span': {
        bottom: 5,
      },

      '& span$tagHome': {
        left: 12,
        transform: 'inherit',

        width: 'auto',
      },
    },
    [theme.breakpoints.match('md')]: {
      position: 'absolute',
      willChange: 'transform',

      width: 300,
      margin: '0 10px',

      '& a': {
        position: 'relative',
      },

      '& img': {
        display: 'block',
      },

      '& span,p': {
        position: 'absolute',
        bottom: 10,
        left: '50%',
        transform: 'translateX(-50%)',

        width: 'max-content',

        ...theme.typography.p3,
        color: '#283333',
        fontStyle: 'italic',
      },

      '& span': {
        bottom: 5,
      },

      '& span$tagHome': {
        left: 12,
        transform: 'inherit',

        width: 'auto',
      },
    },
  },
  tagHome: {},
}));

const dataFrom = 'block-products-push';

/**
 * Mobile collection
 */

const FullWidthSection = styled.section`
  position: relative;

  width: 100%;

  overflow: hidden;
  background-color: #ededf3;

  & a {
    margin-top: 30px;
    text-decoration: none;
  }

  ${props => props.theme.breakpoints.match('sm')} {
    padding-bottom: 72px;

    & a {
      display: block;
      margin: auto;
      margin-top: 30px;
    }
  }
`;

const FullWidthTopBlock = styled.div`
  height: 55vh;
  padding: ${props => props.theme.spacing.s72}px 0;

  background-image: ${({ img }) => `url(${img})`};
  background-size: cover;

  & h1 {
    display: block;
    padding: 0 24px;

    text-align: center;

    & button {
      display: block;
      margin: 20px auto;
    }
  }
`;

const ProductsCardsContainer = styled.div`
  ${props => props.theme.breakpoints.match('md')} {
    width: 100%;
    min-height: 440px;
    margin-top: -360px;
  }

  ${props => props.theme.breakpoints.match('sm')} {
    width: 100%;
    min-height: 360px;
    margin-top: -100px;
  }
`;

const MobileCollection = ({ products, curatedCollection }) => {
  const cardWidth = 300;
  const gutterSize = 20;
  const calculatedGutter = cardIndex => (cardIndex + 1) * gutterSize;

  const index = useRef(0);
  const [cards, setCards] = useSprings(curatedCollection.length, i => ({
    x: i * cardWidth + calculatedGutter(i),
  }));
  const classes = useStyles();
  // Function that get the touch and mouse events and translate them into 'custom events'
  const bind = useGesture(({ down, direction: [xDir], distance, cancel }) => {
    // When down and swipping update index of my slider to 1 or -1
    if (down && distance > 100) {
      cancel(
        (index.current = clamp(
          0,
          curatedCollection.length - 1,
          index.current + (xDir > 0 ? -1 : 1)
        ))
      );
    }
    setCards(i => {
      const x = (i - index.current) * cardWidth + calculatedGutter(i);
      return { x };
    });
  });

  return (
    <FullWidthSection>
      <FullWidthTopBlock img={products.ad.mobileImg}>
        <Typography color="white" variant="h1">
          {products.ad.headline}
          <Button
            color="white"
            Component={Link}
            data-click="products"
            data-from={dataFrom}
            to="/products"
            variant="underlinedWhite"
          >
            {products.ad.cta}
          </Button>
        </Typography>
      </FullWidthTopBlock>

      <ProductsCardsContainer>
        {cards.map(({ x, display }, i) => {
          const isSupplements = curatedCollection[i].slug === productsSlugs.SUPPLEMENT_CORE;

          return (
            <a.div
              key={curatedCollection[i].slug}
              className={classes.wrapper}
              style={{
                display,
                transform: x.interpolate(xAxis => `translate3d(${xAxis}px,0,0)`),
              }}
              {...bind()}
            >
              <ProductsItem
                key={curatedCollection[i].slug}
                bgLabelledPicture={
                  // Inverted on purpose (Product Team decision)
                  isSupplements
                    ? curatedCollection[i].bgPicture
                    : curatedCollection[i].bgLabelledPicture
                }
                bgPicture={
                  // Inverted on purpose (Product Team decision)
                  isSupplements
                    ? curatedCollection[i].bgLabelledPicture
                    : curatedCollection[i].bgPicture
                }
                capacity={curatedCollection[i].capacity}
                capacityVariant="p3"
                cardTag={curatedCollection[i].cardTag}
                classes={{
                  card: classes.cardHome,
                  imageContainer: classes.imageContainerHome,
                  tag: classes.tagHome,
                }}
                dataFrom={dataFrom}
                displayCTA={curatedCollection[i].displayCTA}
                /* Enabling it triggers a misalignment of label and capacity. The card tag has a redundant role anyway. */
                isNew={false}
                label={`Custom ${curatedCollection[i].label}`}
                labelVariant="p3"
                price={curatedCollection[i].price}
                productPageUrl={curatedCollection[i].productPageUrl}
                slug={curatedCollection[i].slug}
              />
            </a.div>
          );
        })}
      </ProductsCardsContainer>
    </FullWidthSection>
  );
};

MobileCollection.propTypes = {
  curatedCollection: PropTypes.arrayOf(PropTypes.object).isRequired,
  products: PropTypes.shape({
    ad: PropTypes.shape({
      alt: PropTypes.string,
      cta: PropTypes.string,
      headline: PropTypes.string,
      img: srcOrNextImageProp,
      mobileImg: srcOrNextImageProp,
    }),
  }).isRequired,
};

/**
 * Desktop collection
 */

const GridItem = styled.div`
  position: relative;

  display: grid;
  grid-template-columns: minmax(0, 1.2fr) 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  grid-column-gap: 25px;
  grid-row-gap: 25px;
  width: 100%;
`;

const ItemA = styled.div`
  position: relative;

  grid-column-start: 1;
  grid-column-end: 2;
  grid-row-start: 1;
  grid-row-end: span 2;
  overflow: hidden;

  & img {
    position: absolute;
    transform: translateX(-50%);

    height: 100%;
  }

  & h1 {
    position: absolute;
    bottom: 20%;
    z-index: 2;

    font-size: 48px;

    & a {
      display: block;
      margin: auto;
    }
  }
`;

const ItemB = styled.div`
  position: relative;

  & img {
    height: 420px;
    display: block;
  }

  & span,
  & p {
    position: absolute;
    bottom: 10px;

    color: #283333;
    font-style: italic;
    font-family: 'Simplon Norm';
    line-height: 1.5em;
    font-size: 12px;
    font-weight: 400;
    letter-spacing: 1px;
  }

  & span {
    bottom: 5px;
  }
`;

const DesktopCollection = ({ curatedCollection, products }) => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });
  const classes = useStyles();
  return (
    <BlockLayout
      ref={ref}
      classes={{
        root: classNames(classes.root, {
          [classes.visible]: inView === true,
        }),
      }}
      contentAlign="center"
      contentWidth="veryWide"
    >
      <GridItem>
        <ItemA>
          <LazyImg alt={products.ad.alt} src={products.ad.img} />
          <Typography color="white" variant="h1">
            {products.ad.headline}
            <br />
            <br />
            <Button
              color="white"
              Component={Link}
              data-click="products"
              data-from={dataFrom}
              to="/products"
              variant="underlinedWhite"
            >
              {products.ad.cta}
            </Button>
          </Typography>
        </ItemA>

        {map(
          ({
            slug,
            bgLabelledPicture,
            bgPicture,
            capacity,
            cardTag,
            displayCTA,
            label,
            price,
            productPageUrl,
          }) => (
            <ItemB key={slug}>
              <ProductsItem
                key={slug}
                bgLabelledPicture={bgLabelledPicture}
                bgPicture={bgPicture}
                capacity={capacity}
                capacityVariant="p3"
                cardTag={cardTag}
                classes={{
                  card: classes.cardHome,
                  imageContainer: classes.imageContainerHome,
                }}
                dataFrom={dataFrom}
                displayCTA={displayCTA}
                // Enabling it triggers a misalignment of label and capacity. The card tag has a redundant role anyway
                isNew={false}
                label={`Custom ${label}`}
                labelVariant="p3"
                price={price}
                productPageUrl={productPageUrl}
                slug={slug}
              />
            </ItemB>
          ),
          curatedCollection
        )}
      </GridItem>
    </BlockLayout>
  );
};

DesktopCollection.propTypes = {
  products: PropTypes.shape({
    ad: PropTypes.shape({
      alt: PropTypes.string,
      cta: PropTypes.string,
      headline: PropTypes.string,
      img: srcOrNextImageProp,
      mobileImg: srcOrNextImageProp,
    }),
  }).isRequired,
  curatedCollection: PropTypes.arrayOf(PropTypes.object).isRequired,
};

/**
 * Parent collection
 */

const Collection = ({ content }) => {
  const isMobile = useMedia(`(max-width: ${breakpoints.sm}px)`);
  const isTablet = useMedia(`(max-width: ${breakpoints.md - 1}px)`);

  const shouldShowStylingGel = useSelector(featureFlagSelectors.shouldShowStylingGel);
  const classes = useStyles();
  // Different rules on what to show in the homepage collections for different feature flags.
  let curatedCollection = [
    { ...supplement_core, productPageUrl: '/pages/supplements' },
    shampoo,
    conditioner,
    scalp_mask,
  ];

  if (shouldShowStylingGel) {
    curatedCollection = [styling_gel, shampoo, conditioner, scalp_mask];
  }

  const { products } = content;

  return !isMobile && !isTablet ? (
    <DesktopCollection
      classes={classes}
      curatedCollection={curatedCollection}
      products={products}
    />
  ) : (
    <MobileCollection classes={classes} curatedCollection={curatedCollection} products={products} />
  );
};

Collection.propTypes = {
  content: PropTypes.shape({
    products: PropTypes.shape({
      ad: PropTypes.shape({
        alt: PropTypes.string,
        cta: PropTypes.string,
        headline: PropTypes.string,
        img: srcOrNextImageProp,
        mobileImg: srcOrNextImageProp,
      }),
    }).isRequired,
  }).isRequired,
};

export default Collection;
